import {
    Component,
    Input,
    ViewChild,
    ViewEncapsulation,
    OnInit,
    AfterViewInit, ViewChildren, QueryList, ChangeDetectorRef, Output, EventEmitter, DestroyRef, inject
} from '@angular/core';
import { DynamicMenuFormComponent } from '@emm/shared/components/dynamic-form/dynamic-menu-form/dynamic-menu-form.component';
import {FormControl, FormGroup, Validators} from '@angular/forms';
import { FormFieldBase } from '@emm/shared/interfaces/form-field-base/form-field-base';
import {filter, ignoreElements, map, skip, startWith, switchMap, take, tap, withLatestFrom} from 'rxjs/operators';
import {BehaviorSubject, combineLatestWith, concatWith, Observable} from 'rxjs';
import * as RA from 'ramda-adjunct';
import {MatDialog} from '@angular/material/dialog';
import * as R from 'ramda';
import {SharedFacadeService} from '@emm/shared/services/shared-facade.service';
import {AttributeGroup} from '@emm/core/models/attribute-group.model';
import {MenuParamsContext} from '@emm/core/models/menu-key.model';
import {MenuTreeFacadeService} from '@emm/menu/services/menu-tree-facade.service';
import {MenuFormFacadeService} from '@emm/menu/services/menu-form-facade.service';
import {NotificationService} from '@emm/shared/services/notification.service';
import {takeUntilDestroyed} from '@angular/core/rxjs-interop';
import {getSortedAttributes, ItemAttribute} from '@emm/core/models/item-attribute.model';
import {ItemDetail} from '@emm/core/models/item-detail.model';
import {ItemAttributeType} from '@emm/core/models/enums';
import {
    UpdateCascadeItemDialogComponent
} from '@emm/shared/dialogs/update-cascade-item-dialog/update-cascade-item-dialog.component';
import {itemAttributeFieldMap as fieldMap} from '@emm/shared/formly/maps/item-attribute.config';
import {TreeNode} from '@ali-hm/angular-tree-component';
import {API_ERRORS} from '@emm/core/util/api-constants';
import {UpdateItemDialogComponent} from '@emm/shared/dialogs/update-item-dialog/update-item-dialog.component';
import {formatDateToMidnight} from '@emm/core/util/menu-date.util';


const ITEM_WIDTH = '2000px';
const ITEM_HEIGHT = '800px';


const LEGEND_ITEMS = [{ color: '#B5E9FD', label: 'Global Value' }, { color: '#EBF9FF', label: 'Overridden Value' }];
const DARK_MODE_LEGEND_ITEMS = [{ color: '#4A4A4A', label: 'Global Value' }, { color: '#5B6770', label: 'Overridden Value' }];

@Component({
    encapsulation: ViewEncapsulation.None,
    selector: 'emm-attributes-form',
    styleUrls: ['./attributes-form.component.scss'],
    templateUrl: './attributes-form.component.html',
    standalone: false
})
export class AttributesFormComponent implements OnInit, AfterViewInit {

    private readonly destroyRef: DestroyRef = inject(DestroyRef);

    // todo: types
    @Input() rightPaneWidth: string;
    @Input() attributeGroups: any;
    @Input() attributesByGroup: any;
    @Input() form: FormGroup;
    @Input() isReadOnly$: any;
    @Input() item: any;
    @Input() selectedSubmenu: string;
    @Input() nodeId: string;
    @Input() menuParams: MenuParamsContext;
    @Input() model: any;
    @Input() options: any;
    @Input() featureScope: string;
    @Input() effectiveDate: string;
    @Input() selectedFeatureScope$: any;
    @Input() dialogService: any;
    @Input() locationSet: any;
    @Input() itemNode: TreeNode;
    @Input() isSubMenu: boolean;
    @Input() language: string;
    @Input() previousAttributeGroups: AttributeGroup[];
    @Output() attributeGroupSelected = new EventEmitter<AttributeGroup[]>();
    @Output() submenuRenamed = new EventEmitter<boolean>();

    @ViewChild(DynamicMenuFormComponent) dynamicForm: DynamicMenuFormComponent;
    @ViewChildren(DynamicMenuFormComponent) dynamicForms: QueryList<DynamicMenuFormComponent>;

    attributes$: Observable<any>;
    private attributesRemoved = {};
    disabled$: Observable<any>;
    existingUpdates$ = new BehaviorSubject<[]>(null);
    existingUpdates;
    fields$: Observable<any>;
    isChecked: boolean;
    isGlobal: boolean;
    isLoading$: Observable<any> = this.formFacade.isLoading$;
    isOpen: boolean;
    item$: Observable<any> = this.formFacade.selectedItemDetail$;
    showCommonItems$: Observable<boolean> = this.formFacade.showCommonItems$;
    isDarkMode = false;
    legendItems = LEGEND_ITEMS;
    localAttributeForm: FormGroup = new FormGroup({});
    selectedStatus: string;
    attributeAdded = false;
    formsBuilt = new BehaviorSubject<boolean>(false);
    localAttributeFormLength = 0;
    buttonsDisabled = true;
    newUpdateFlow$ = this.formFacade.newUpdateFlow$;

    constructor(public treeFacade: MenuTreeFacadeService,
                private formFacade: MenuFormFacadeService,
                private dialog: MatDialog,
                private changeDetector: ChangeDetectorRef,
                private sharedFacade: SharedFacadeService,
                private notificationService: NotificationService,
    ) {}


    ngOnInit() {
        this.sharedFacade.currentTheme$
            .pipe(
                takeUntilDestroyed(this.destroyRef)
            )
            .subscribe(theme => {
            theme === 'dark-theme' ? this.isDarkMode = true : this.isDarkMode = false;
            this.legendItems = this.isDarkMode ? DARK_MODE_LEGEND_ITEMS : LEGEND_ITEMS;
        });
        this.attributes$ = this.setAttributesSubscription();
        this.disabled$ = this.setDisabledSubscription();
        this.setFieldsSubscription();
        this.showCommonItems$.pipe(takeUntilDestroyed(this.destroyRef)).subscribe();
    }


    ngAfterViewInit() {
        this.dynamicForms.changes
            .pipe(
                takeUntilDestroyed(this.destroyRef)
            )
            .subscribe(() => {
            try {
                this.localAttributeForm = this.dynamicForms.first.form;
            } catch(e) { }
            this.areFormsBuilt();
            this.changeDetector.detectChanges();
        });
    }


    setAttributesSubscription(): Observable<any> {
        return this.item$.pipe(
            filter(RA.isNotNil),
            startWith([] as ItemAttribute[]),
            map((item: ItemDetail) => item.attributes)
        );
    }


    setDisabledSubscription(): Observable<any> {
        return this.isReadOnly$.pipe(
            combineLatestWith(this.isLoading$),
            map((sources: [boolean, Observable<boolean>]) => R.any(R.equals(true), sources))
        );
    }


    setFieldsSubscription(): void {
        this.fields$ = this.attributes$.pipe(
            tap(() => {
                if (this.options && this.options.resetModel && this.options.updateInitialValue) {
                    this.options.resetModel();
                    this.options.updateInitialValue();
                }
            }),
            filter(RA.isNonEmptyArray),
            withLatestFrom(this.isReadOnly$, this.sharedFacade.localeSelected$),
            map(([attributes, isReadOnly, localeSelected]) => {
                if (!localeSelected) {
                    return [];
                }
                return getSortedAttributes<ItemAttribute>(attributes)
                    .filter(({type}) => Object.values(ItemAttributeType).includes(type))
                    .filter(({type}) => Object.keys(fieldMap).includes(type))
                    .map((attribute: ItemAttribute, index: number) => {
                        const mapper = fieldMap[attribute.type];
                        return {
                            config: mapper(attribute, index, !this.isGlobal, localeSelected.language),
                            attribute
                        };
                    })
                    .map(({config, attribute}) => {
                        return {
                            ...config,
                            templateOptions: {
                                ...config.templateOptions,
                                showLocaleIcons: false,
                                readonly: !this.isGlobal,
                            }
                        };
                    });
            }),
            takeUntilDestroyed(this.destroyRef)
        );
    }


    handleNewAttributeSelection(selection: any): void {
        this.addLocalAttribute(selection);
    }


    handleRemoveLocalAttribute(formField: FormFieldBase<string>) {
        this.removeLocalAttribute(formField);

    }

    cancel(attributeGroupId?: string): void {
        this.dynamicForms
            .filter(df => {
                if (!df.form.dirty) {
                    return false;
                }
                if (attributeGroupId) {
                    return df.attributeGroupId === attributeGroupId;
                }
                return true;
            })
            .forEach(df => {
                this.model = df.model;
                df.form.reset(this.model);
                df.form.markAsPristine();
                this.buttonsDisabled = true;
            })
    }


    cancelAllDirtyAttributeGroups(): void {
        if (this.dynamicForms) {
            this.dynamicForms.forEach(df => {
                if (df.form.dirty) {
                    this.cancel(df.attributeGroupId)
                }
            });
        }
    }


    getAllDirtyAttributes() {
        let mergedUpdates;
        let newUpdates;
        this.dynamicForms
            .filter(df => {
                return df.form.dirty;
            })
            .forEach(df => {
                newUpdates = {...newUpdates, ...this.getDirtyValues(df.form)}
                if (this.existingUpdates !== undefined) {
                    mergedUpdates = {...mergedUpdates, ...newUpdates, ...this.existingUpdates};
                    this.existingUpdates = null;
                } else {
                    mergedUpdates = {...mergedUpdates, ...newUpdates};
                }

                Object.keys(mergedUpdates).forEach((key) => {
                    if (typeof (mergedUpdates === undefined ? newUpdates[key] === 'string' : mergedUpdates[key]) === 'string') {
                        if (mergedUpdates === undefined) {
                            newUpdates[key] = newUpdates[key].trim();
                        } else {
                            mergedUpdates[key] = mergedUpdates[key].trim();
                        }
                    }
                });
            });
        return mergedUpdates === undefined ? newUpdates : mergedUpdates;
    }


    submit(reloadAfterUpdate: boolean = true): void {
        console.log('[AttributesForm] submit called - reloadAfterUpdate:', reloadAfterUpdate);
        if (this.dynamicForms.first.form.invalid || this.hasInvalidSubsGroupId()) {
            if (this.hasInvalidSubsGroupId()) {
                this.notificationService.error(`If an item has an RMI, it must also have a Subs Group ID`);
                return;
            }
            this.notificationService.error(`Please fill out all required fields.`);
            return;
        }
        let removedAttributeUpdates;
        const dynamicForm = this.dynamicForms.find(df => df.attributeGroupId === 'DYNAMIC');
        const dynamicAttributes: Record<string, any> = {};
        if (dynamicForm) {
            Object.entries(dynamicForm.form?.controls).forEach(([controlName, control]) => {
                dynamicAttributes[controlName] = control.value;
            });
        } else {
            this.notificationService.error('Dynamic form with attributeGroupId "DYNAMIC" not found.');
        }
               for (const attribute in this.attributesRemoved) {
            removedAttributeUpdates = {...removedAttributeUpdates, [attribute]: null};
        }
        const mergedUpdates = {...this.getAllDirtyAttributes(), ...removedAttributeUpdates, ...dynamicAttributes};

        const modifierTypeRequired = this.item?.fieldRules?.modifierType?.required;
        if (modifierTypeRequired && !mergedUpdates['modifierType']) {
            this.notificationService.error(`${this.item.typeName} require a Modifier Type. Please add one before submitting changes.`);
            return;
        }

        if (this.isSubMenu && mergedUpdates['name']) {
            this.submenuRenamed.emit(true);
            this.treeFacade.renameSubmenu(this.item.tag, mergedUpdates['name']);
        }

        const currentItem$ = this.item$.pipe(
            take(1),
            tap((item) => {
                console.log('[AttributesForm] calling updateItem - reloadAfterUpdate:', reloadAfterUpdate);
                this.formFacade.updateItem(item, mergedUpdates, reloadAfterUpdate, this.isChecked);
            })
        );
        const updatedItem$ = this.item$.pipe(skip(1), take(1));
        currentItem$.pipe(
            ignoreElements(),
            concatWith(updatedItem$))
            .pipe(
                takeUntilDestroyed(this.destroyRef)
            )
            .subscribe({
                next: (item: ItemDetail) => {
                    this.treeFacade.reloadItems(item?.tagPath);
                },
            });
    }

    updateItem(): void {
        let removedAttributeUpdates;
               const dynamicForm = this.dynamicForms.find(df => df.attributeGroupId === 'DYNAMIC');
        const dynamicAttributes: Record<string, any> = {};
        if (dynamicForm) {
            Object.entries(dynamicForm.form.controls).forEach(([controlName, control]) => {
                dynamicAttributes[controlName] = control.value;
            });
        } else {
            this.notificationService.error('Dynamic form with attributeGroupId "DYNAMIC" not found.');
        }

               for (const attribute in this.attributesRemoved) {
            removedAttributeUpdates = {...removedAttributeUpdates, [attribute]: null};
        }
        const updates = {...this.getAllDirtyAttributes(), ...removedAttributeUpdates, ...dynamicAttributes};
        const modifierTypeRequired = this.item?.fieldRules?.modifierType?.required;
        if (modifierTypeRequired && !updates['modifierType']) {
            this.notificationService.error(`${this.item.typeName} require a Modifier Type. Please add one before submitting changes.`);
            return;
        } else {
            this.formFacade.menuParamsContext$
                .pipe(
                    take(1),
                    withLatestFrom(this.selectedFeatureScope$, this.item$),
                    switchMap(([{effectiveDate, menuKey}, featureScope, itemDetail]) => {
                            return this.dialog
                                .open(UpdateItemDialogComponent, {
                                    height: ITEM_HEIGHT,
                                    width: ITEM_WIDTH,
                                    data: {
                                        featureScope,
                                        effectiveDate: formatDateToMidnight(effectiveDate),
                                        itemDetail,
                                        item: this.itemNode,
                                        menuKey,
                                        updates,
                                        locationSet: this.locationSet,
                                    },
                                })
                                .afterClosed();
                        }
                    ),
                    tap((result) => {
                        this.itemNode = Object(result).keys.length > 0 ? result?.item as TreeNode : null;
                    }),
                    tap(() => this.dynamicForms.forEach(df => df.form.markAsPristine())),
                    takeUntilDestroyed(this.destroyRef)
                )
                .subscribe({
                    next: (item: ItemDetail) => {
                        this.treeFacade.reloadItems(item?.tagPath);
                    },
                });
        }
    }


    updateItemCascade(): void {
        let removedAttributeUpdates;
               const dynamicForm = this.dynamicForms.find(df => df.attributeGroupId === 'DYNAMIC');
        const dynamicAttributes: Record<string, any> = {};
        if (dynamicForm) {
            Object.entries(dynamicForm.form.controls).forEach(([controlName, control]) => {
                dynamicAttributes[controlName] = control.value;
            });
        } else {
            this.notificationService.error('Dynamic form with attributeGroupId "DYNAMIC" not found.');
        }

               for (const attribute in this.attributesRemoved) {
            removedAttributeUpdates = {...removedAttributeUpdates, [attribute]: null};
        }
        const updates = {...this.getAllDirtyAttributes(), ...removedAttributeUpdates, ...dynamicAttributes};
        const modifierTypeRequired = this.item?.fieldRules?.modifierType?.required;
        if (modifierTypeRequired && !updates['modifierType']) {
            this.notificationService.error(`${this.item.typeName} require a Modifier Type. Please add one before submitting changes.`);
            return;
        } else {
            this.formFacade.menuParamsContext$
                .pipe(
                    take(1),
                    withLatestFrom(this.selectedFeatureScope$, this.item$),
                    switchMap(([{effectiveDate, menuKey}, featureScope, itemDetail]) => {
                            return this.dialog
                                .open(UpdateCascadeItemDialogComponent, {
                                    height: ITEM_HEIGHT,
                                    width: ITEM_WIDTH,
                                    data: {
                                        featureScope,
                                        effectiveDate: formatDateToMidnight(effectiveDate),
                                        itemDetail,
                                        item: this.itemNode,
                                        menuKey,
                                        updates,
                                        forceOverrides: this.isChecked,
                                        locationSet: this.locationSet,
                                    },
                                })
                                .afterClosed();
                        }
                    ),
                    tap((result) => {
                        this.itemNode = result.item as TreeNode ?? null;
                    }),
                    tap(() => this.dynamicForms.forEach(df => df.form.markAsPristine())),
                    takeUntilDestroyed(this.destroyRef)
                )
                .subscribe({
                    next: (item: ItemDetail) => {
                        this.treeFacade.reloadItems(item?.tagPath);
                    },
                });
        }
    }


    addLocalAttribute($event): void {
        const index = $event.value;
        this.existingUpdates = this.getDirtyValues(this.localAttributeForm);
        this.existingUpdates$.next(this.existingUpdates);
        this.changeDetector.detectChanges();
        this.item$
            .pipe(
                take(1),
                filter(RA.isNotNil),
                map((item: ItemDetail) => item.unassigned),
                filter(RA.isNonEmptyArray),
                takeUntilDestroyed(this.destroyRef)
            )
            .subscribe((attributes) => {
                this.handleAddLocalAttribute(attributes[index], index, this.localAttributeForm).then(() => {
                    if (this.localAttributeForm) {
                        this.localAttributeForm.markAsDirty();
                    }
                    this.changeDetector.detectChanges();
                });
            });
        $event.source.value = '';
        this.attributeAdded = true;
    }


    async handleAddLocalAttribute(selectedAttribute, index, form) {
        delete this.attributesRemoved[selectedAttribute.jsonProperty];
        form.addControl(selectedAttribute.jsonProperty, selectedAttribute.required ? new FormControl({
                value: selectedAttribute.value, disabled: false, markAsDirty: true, dirty: true}, Validators.required) :
            new FormControl({value: selectedAttribute.value, disabled: false, markAsDirty: true, dirty: true}));
        this.formFacade.addLocalAttribute(index);
        form.markAsDirty();
        await this.changeDetector.detectChanges();
    }


    submitLocalAttribute(reloadAfterUpdate: boolean = true): void {
        const newUpdates = {...this.getDirtyValues(this.localAttributeForm)}
        const currentItem$ = this.item$.pipe(
            take(1),
            tap((item) => {
                this.formFacade.updateItem(item, newUpdates, reloadAfterUpdate, false);
            })
        );
        const updatedItem$ = this.item$.pipe(skip(1), take(1));
        currentItem$.pipe(
            ignoreElements(),
            concatWith(updatedItem$),
            takeUntilDestroyed(this.destroyRef))
            .subscribe({
                next: (item: ItemDetail) => {
                    try {
                        this.localAttributeForm.markAsPristine();
                        this.notificationService.success(`${item.name} was successfully updated`);
                    } catch {
                        this.notificationService.error(`${item.name} was successfully updated, but the form did not reload properly. Please refresh the page.`);
                    }
                },
                error: (error) => {
                    this.notificationService.error(error?.error?.error.message || API_ERRORS.GenericErrorMessage);
                }
            });
        this.attributeAdded = false;
    }


    removeLocalAttribute(formField: FormFieldBase<string>) {
        let index;
        const attributes = [];
        Object.keys(this.localAttributeForm.controls).forEach(controlName => attributes.push(controlName));
        this.attributesRemoved[formField.jsonProperty] = formField;

               for (let i = 0; i < attributes.length; i++) {
            if (attributes[i] === formField.jsonProperty) {
                index = i;
            }
        }
        this.localAttributeForm.removeControl(formField.jsonProperty);
        this.formFacade.removeLocalAttribute(index);
        this.dynamicForms.last.form.markAsDirty();
        this.buttonsDisabled = false;
    }


    getDirtyValues(form: any): {} {
        const dirtyValues = {};
        if (form) {
            Object.keys(form.controls).forEach((key) => {
                const currentControl = form.controls[key];
                if (currentControl.dirty) {
                    if (currentControl.controls) {
                        dirtyValues[key] = this.getDirtyValues(currentControl);
                    } else {
                        dirtyValues[key] = currentControl.value;
                    }
                }
            });

            Object.keys(this.attributesRemoved).forEach((key) => {
                dirtyValues[key] = null;
                delete this.attributesRemoved[key];
            });
        }

        return dirtyValues;
    }

    forceOverrideInheritance($event): any {
        this.isChecked = $event;
    }

    isFormReady(): boolean {
        return this.dynamicForm.isFormReady();
    }

    hasChanges(): boolean {
        if (this.dynamicForms) {
            this.dynamicForms.forEach(df => {
                if (df.form?.dirty) {
                    this.localAttributeForm.markAsDirty();
                    this.buttonsDisabled = false;
                }
            });
        }
        return !this.buttonsDisabled;
    }

    hasValue(attributeGroupId: string): boolean {
        const attributesByGroup = this.attributesByGroup.get(attributeGroupId);
        if (!attributesByGroup.getValue()) {
            return false;
        }
        return attributesByGroup.getValue().length !== 0;
    }


    openPanel(status): void {
        this.selectedStatus = status;
        if (!this.isPreviousPanel(status)) {
            this.previousAttributeGroups.push(status);
        }
        this.isOpen = true;
        this.attributeGroupSelected.next(this.previousAttributeGroups);
    }


    closePanel(status): void {
        this.selectedStatus = null;
        this.previousAttributeGroups.find((item, index) => item === status && this.previousAttributeGroups.splice(index, 1));
        this.isOpen = false;
        this.attributeGroupSelected.next(this.previousAttributeGroups);
    }

    isPreviousPanel(status) {
        return this.previousAttributeGroups.some(i => i.name === status.name);
    }

    areFormsBuilt(): boolean {
        if (this.dynamicForms && this.dynamicForms.length && this.dynamicForms.length > 0) {
            this.formsBuilt.next(true);
            this.localAttributeFormLength = Object.keys(this.localAttributeForm?.controls)?.length;
        } else {
            this.formsBuilt.next(false);
        }
        return this.formsBuilt.value;
    }

    hasInvalidImageLink(): boolean {
        let result = false;
        if (this.dynamicForms) {
            const imageForm = this.dynamicForms?.find((df) =>
                df?.attributeGroupId === 'IMAGE');
            result = imageForm?.form?.invalid;
        }
        return result;
    }

    hasInvalidDefaultTag(): boolean {
        let result = false;
        if (this.dynamicForms) {
            const dynamicAttributesForm = this.dynamicForms?.find((df) =>
                df?.attributeGroupId === 'CORE');
            result = dynamicAttributesForm?.form?.controls['defaultTag']?.invalid;
        }
        return result;
    }

    hasInvalidSubsGroupId(): boolean {
        let result = false;
        if (this.dynamicForms) {
            const coreForm = this.dynamicForms?.find((df) =>
                df?.attributeGroupId === 'CORE');
            result = coreForm?.form?.controls['subsGroupId']?.invalid;
        }
        return result;
    }

    hasInvalidFormfield() {
        return this.hasInvalidImageLink() || this.hasInvalidDefaultTag() || this.hasInvalidSubsGroupId();
    }
}
